import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { PaidPlan, SubscriptionResponse, SupportedLocale } from '../../lib/api/types.js';
import type { ProfileVisibility } from '@cavaquinho/shared';
import { api } from '../../lib/api/client.js';
import { useAuth, useCurrentUser } from '../../app/AuthProvider.tsx';
import { setDevRole } from '../../app/dev-auth.js';
import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Field } from '../../ui/primitives/Field.tsx';
import { Input } from '../../ui/primitives/Input.tsx';
import { LiveRegion } from '../../ui/primitives/LiveRegion.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { Dialog } from '../../ui/components/Dialog/Dialog.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { noteLabel } from '../../lib/notes.js';
import { AccountIdentity } from './AccountIdentity.tsx';
import { SettingChoice } from './SettingChoice.tsx';
import styles from './account.module.css';

/**
 * The learner's own account (T105; FR-072, FR-075, FR-077, SC-019).
 *
 * ONE RULE, AND THE WHOLE PAGE IS BUILT ON IT:
 *
 *   A VALUE is a control you change. A VERB is a button you press.
 *
 * Values — the name, who may see the profile, how notes are spelled — are
 * fields and tiles, edited in place, saved on the spot. There is no "Editar"
 * in front of a text field and no "Salvar" behind a choice, because both are
 * steps in front of a control that was already there.
 *
 * Verbs — download, erase, cancel, leave, delete — are pills at the LEFT
 * margin, under the sentence that says what they do. That is the answer to
 * the thing that made this screen look improvised: with nothing reaching for
 * the right edge, no control's position depends on how long its sentence
 * happens to be, and the page reads the same at 360px and at 1440.
 *
 * THE COMPOSITION IS `/perfil`'s, DELIBERATELY. At 1024 the identity leaves
 * the flow into a sticky rail and the settings run down the main column —
 * the same grid `directions/Editorial.tsx` gives the Profile. The two screens
 * about you are then one pair with one architecture: `/perfil` is what you
 * have done, `/conta` is what you can change.
 *
 * NO SECTION BOXES AND NO HAIRLINES. § 2 blocks "stacked rectangles as the
 * default layout" and "1px borders as the default separator" by name, and
 * § 1.8 says space separates before rules do. The screen's ONE surface (§ 7)
 * is the plan, which is the only thing here that is a STATE rather than a
 * setting — everything else is text, fields and tiles on the background.
 *
 * THREE DESTRUCTIONS, AND THEY ARE NOT THE SAME SIZE. Signing out ends a
 * session and asks nothing, because a learner who did not mean it signs in
 * again. Erasing keeps the account and empties it, and asks once with the
 * export offered in the same breath. Deleting ends everything and asks for
 * the word to be typed (FR-077). The ceremony is proportional to what cannot
 * be taken back.
 */
const CONFIRMATION_WORD = 'EXCLUIR';

type Busy =
  | 'none'
  | 'name'
  | 'visibility'
  | 'locale'
  | 'export'
  | 'erase'
  | 'sign-out'
  | 'sign-out-everywhere'
  | 'subscribe'
  | 'cancel-plan'
  | 'reactivate-plan'
  | 'delete';

/** Which question is being asked over the page, if any. */
type Asking = 'none' | 'subscribe' | 'cancel-plan' | 'erase' | 'delete';

const PLAN_NAMES: Record<PaidPlan, string> = { monthly: 'Mensal', annual: 'Anual' };

/** `14 de outubro de 2026` — a date read in a sentence, not down a column. */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * A named part of the column: its title, and whatever belongs under it. No
 * surface — the title and the space above it are the whole of the grouping.
 */
function Section({
  title,
  children,
  testId,
}: {
  title: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className={styles.section} data-testid={testId}>
      <Text as="h2" variant="subheading">
        {title}
      </Text>
      {children}
    </section>
  );
}

/** A sentence and the verb it licenses, in that order, at the left margin. */
function Doing({ children, actions }: { children: ReactNode; actions: ReactNode }) {
  return (
    <div className={styles.doing}>
      {typeof children === 'string' ? (
        <Text as="p" variant="body-sm" tone="muted">
          {children}
        </Text>
      ) : (
        children
      )}
      <div className={styles.verbs}>{actions}</div>
    </div>
  );
}

export function AccountPage() {
  const { refresh } = useAuth();
  const user = useCurrentUser();

  const [busy, setBusy] = useState<Busy>('none');
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSaved, setNameSaved] = useState(false);
  const [notice, setNotice] = useState('');
  const [asking, setAsking] = useState<Asking>('none');

  const [typed, setTyped] = useState('');
  const [chosenPlan, setChosenPlan] = useState<PaidPlan>('monthly');
  const [exported, setExported] = useState(false);

  const locale: SupportedLocale = (user?.locale as SupportedLocale) ?? 'pt-BR';
  const visibility: ProfileVisibility = user?.profileVisibility ?? 'public';
  const name = user?.name ?? 'Sua conta';
  const idle = busy === 'none';

  /* "Salvo" is a moment, not a state: it fades on its own and is cleared if
     the screen goes away before it does. */
  const flash = useRef<number | null>(null);
  useEffect(() => () => window.clearTimeout(flash.current ?? undefined), []);

  /*
   * The plan is the one thing on this screen the account itself does not
   * carry: `/api/me` describes who somebody is, and billing is its own
   * resource with its own dates. It is fetched rather than assumed, and a
   * failure to reach it degrades this section alone.
   */
  const {
    status: planStatus,
    data: plan,
    reload: reloadPlan,
    setData: setPlan,
  } = useAsyncData<SubscriptionResponse | null>(() => api.subscription(), null, []);

  /** Every write goes through here, so no two controls disagree about failure. */
  const run = useCallback(
    async (kind: Exclude<Busy, 'none'>, whatFailed: string, action: () => Promise<void>) => {
      if (busy !== 'none') return;
      setBusy(kind);
      setError(null);
      try {
        await action();
      } catch {
        setError(whatFailed);
      } finally {
        setBusy('none');
      }
    },
    [busy],
  );

  /**
   * The name everybody else reads, on a ranking row and in a follow list.
   * `AccountIdentity` has already decided that something changed; this only
   * carries it, and reports its own failure beside the field rather than at
   * the top of a page whose field is out of sight.
   */
  const saveName = (next: string) =>
    void run('name', 'Não foi possível salvar seu nome.', async () => {
      setNameError(null);
      try {
        await api.updateMe({ name: next });
      } catch (cause) {
        setNameError('Não foi possível salvar seu nome.');
        throw cause;
      }
      // The account is resolved once and held, so it is re-read rather than
      // patched locally — the same path returning from any account change.
      await refresh();
      setNotice('Nome salvo.');
      setNameSaved(true);
      window.clearTimeout(flash.current ?? undefined);
      flash.current = window.setTimeout(() => setNameSaved(false), 2400);
    });

  const setVisibility = (next: ProfileVisibility) =>
    next === visibility
      ? undefined
      : void run('visibility', 'Não foi possível mudar quem vê seu perfil.', async () => {
          await api.updateMe({ profileVisibility: next });
          await refresh();
          setNotice(
            next === 'private' ? 'Seu perfil agora é privado.' : 'Seu perfil agora é público.',
          );
        });

  /**
   * FR-075 — presentation, and only presentation. The request touches one
   * column of the learner's own row; every attempt, completion, session and
   * combination-history entry is left exactly as it was, and the change takes
   * effect on the next note name rendered.
   */
  const setLocale = (next: SupportedLocale) =>
    next === locale
      ? undefined
      : void run('locale', 'Não foi possível mudar o idioma das notas.', async () => {
          await api.updateMe({ locale: next });
          await refresh();
          setNotice(next === 'pt-BR' ? 'Notas em Dó, Ré, Mi.' : 'Notas em C, D, E.');
        });

  /**
   * FR-072 — self-service, with no operator step. The learner asks and
   * receives, here, now.
   */
  const exportData = () =>
    void run('export', 'Não foi possível baixar seus dados.', async () => {
      const data = await api.exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'cavaquinho-meus-dados.json';
      anchor.click();
      URL.revokeObjectURL(url);
      setExported(true);
      setNotice('Arquivo baixado.');
    });

  const startPlan = () =>
    void run('subscribe', 'Não foi possível iniciar o plano.', async () => {
      setPlan(await api.startSubscription(chosenPlan));
      setAsking('none');
      setNotice(`Plano ${PLAN_NAMES[chosenPlan]} ativo.`);
    });

  const cancelPlan = () =>
    void run('cancel-plan', 'Não foi possível cancelar o plano.', async () => {
      setPlan(await api.cancelSubscription());
      setAsking('none');
      setNotice('Plano cancelado. O acesso segue até o fim do período pago.');
    });

  const reactivatePlan = () =>
    void run('reactivate-plan', 'Não foi possível reativar o plano.', async () => {
      setPlan(await api.reactivateSubscription());
      setNotice('Plano reativado.');
    });

  /**
   * Erasing without leaving. The account survives; the record does not.
   *
   * The reload is not tidiness: the learner's position, their week and their
   * follow lists are all held in screens that have already resolved, and
   * going on with those on screen would show somebody a progress bar for
   * attempts that no longer exist. The app is started over at the root, which
   * resolves a fresh session at the first Lesson — and the dialog says so
   * before it happens, because an unannounced reposition is exactly what a
   * learner cannot be dropped into.
   */
  const eraseData = () =>
    void run('erase', 'Não foi possível apagar seus dados.', async () => {
      await api.eraseMyData();
      window.location.assign('/');
    });

  /**
   * Ending the session — the only reversible departure on this screen, and
   * therefore the one that asks nothing before it happens. A learner who did
   * not mean it signs in again; a confirmation here would be a step in front
   * of a door that opens both ways.
   *
   * The reload is the same argument `DevRoleSwitcher` makes: `AuthProvider`
   * resolves the account ONCE, at the top of the tree, so leaving is honoured
   * by starting the application over rather than by giving that provider a
   * second way to be told who the person is now.
   */
  const signOut = (everywhere: boolean) =>
    void run(
      everywhere ? 'sign-out-everywhere' : 'sign-out',
      everywhere
        ? 'Não foi possível sair dos outros aparelhos.'
        : 'Não foi possível sair da sua conta.',
      async () => {
        await (everywhere ? api.signOutEverywhere() : api.signOut());
        // The development door remembers a chosen role in `localStorage`, and
        // it is read before /api/me is believed — so leaving it behind would
        // sign this browser straight back in as the stand-in. No-op in a
        // production build, where the module holds no identities at all.
        setDevRole(null);
        window.location.assign('/entrar');
      },
    );

  const deleteAccount = () => {
    if (typed !== CONFIRMATION_WORD) return;
    void run('delete', 'Não foi possível excluir a conta.', async () => {
      await api.deleteMyAccount();
      // Nothing to return to: the account is gone, so the app is reloaded at
      // the root and resolves as a signed-out visitor.
      window.location.assign('/');
    });
  };

  const closeDialog = () => {
    // Mid-request there is nothing a dismissal could undo, and a dialog that
    // vanished while the account was going would leave the learner on a page
    // whose state is already a lie.
    if (busy === 'delete' || busy === 'erase') return;
    setAsking('none');
    setTyped('');
  };

  /** The one alert line, rendered wherever the reader actually is. */
  const alert =
    error && asking === 'none' ? (
      <div role="alert" data-testid="account-error" className={styles.alert}>
        <Text as="p" variant="body-sm" tone="error">
          {error}
        </Text>
      </div>
    ) : null;

  const dialogAlert = error ? (
    <div role="alert" data-testid="account-error">
      <Text as="p" variant="body-sm" tone="error">
        {error}
      </Text>
    </div>
  ) : null;

  return (
    <div className={styles.page} data-testid="account-page">
      <AccountIdentity
        name={name}
        email={user?.email ?? null}
        image={user?.image ?? null}
        busy={busy === 'name'}
        error={nameError}
        saved={nameSaved}
        onSave={saveName}
      />

      <div className={styles.column}>
        {/*
          ONLY WHILE EVERY DIALOG IS SHUT. A failure raised from inside one —
          an export that did not download, a deletion the server refused —
          would be reported here, behind the scrim, where the person who
          caused it cannot see it. So the same line is rendered in the open
          dialog instead, and this one stands down.
        */}
        {alert}

        <SettingChoice<ProfileVisibility>
          legend="Quem vê seu perfil"
          description="Seu nome e sua foto aparecem no ranking e nas listas de quem segue quem, sempre. Isto decide quem vê os números por trás deles."
          name="visibility"
          value={visibility}
          busy={!idle}
          onChange={setVisibility}
          options={[
            {
              value: 'public',
              label: 'Público',
              hint: 'Qualquer pessoa vê seus pontos, sua ofensiva e seus pódios.',
            },
            {
              value: 'private',
              label: 'Privado',
              hint: 'Só você vê seus números. Seu nome continua no ranking.',
            },
          ]}
        />

        {/*
          The choice shown in its own terms rather than described: the names
          themselves ARE what the setting decides, which is why they are the
          tile's content at `token-lg` and not a sentence about them.
        */}
        <SettingChoice<SupportedLocale>
          legend="Nomes das notas"
          description={
            <span data-testid="locale-preview">
              <Text as="p" variant="body-sm" tone="muted">
                Como cada nota é escrita nos exercícios. Nada do que você já respondeu muda — hoje
                elas aparecem como {noteLabel('C', locale)}, {noteLabel('E', locale)} e{' '}
                {noteLabel('G', locale)}.
              </Text>
            </span>
          }
          name="locale"
          value={locale}
          busy={!idle}
          onChange={setLocale}
          options={[
            { value: 'pt-BR', label: 'Dó · Ré · Mi', emphasis: 'token', hint: 'Português' },
            { value: 'en', label: 'C · D · E', emphasis: 'token', hint: 'Cifra' },
          ]}
        />

        <Section title="Plano" testId="plan-section">
          <PlanCard
            plan={plan}
            status={planStatus}
            idle={idle}
            busy={busy}
            onRetry={() => reloadPlan({ force: true })}
            onSubscribe={() => setAsking('subscribe')}
            onCancel={() => setAsking('cancel-plan')}
            onReactivate={reactivatePlan}
          />
        </Section>

        <Section title="Seus dados" testId="data-section">
          <Doing
            actions={
              <span data-testid="export-data">
                {idle ? (
                  <Button variant="secondary" size="compact" icon="download" onClick={exportData}>
                    {exported ? 'Baixar de novo' : 'Baixar meus dados'}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="compact"
                    icon="download"
                    disabled
                    disabledReason={busy === 'export' ? 'Preparando o arquivo.' : 'Aguarde.'}
                  >
                    {busy === 'export' ? 'Preparando…' : 'Baixar meus dados'}
                  </Button>
                )}
              </span>
            }
          >
            Um arquivo com tudo o que a plataforma guarda sobre você.
          </Doing>

          <Doing
            actions={
              <span data-testid="start-erase">
                {idle ? (
                  <Button variant="secondary" size="compact" onClick={() => setAsking('erase')}>
                    Apagar meus dados
                  </Button>
                ) : (
                  <Button variant="secondary" size="compact" disabled disabledReason="Aguarde.">
                    Apagar meus dados
                  </Button>
                )}
              </span>
            }
          >
            Apaga seu progresso e suas conexões, e mantém sua conta e suas preferências.
          </Doing>
        </Section>

        <Section title="Acesso" testId="access-section">
          <Doing
            actions={
              <>
                <span data-testid="sign-out">
                  {idle ? (
                    <Button
                      id="sign-out"
                      variant="secondary"
                      size="compact"
                      icon="log-out"
                      onClick={() => signOut(false)}
                    >
                      Sair
                    </Button>
                  ) : (
                    <Button
                      id="sign-out"
                      variant="secondary"
                      size="compact"
                      icon="log-out"
                      disabled
                      disabledReason={busy === 'sign-out' ? 'Saindo.' : 'Aguarde.'}
                    >
                      {busy === 'sign-out' ? 'Saindo…' : 'Sair'}
                    </Button>
                  )}
                </span>

                <span data-testid="sign-out-everywhere">
                  {idle ? (
                    <Button variant="ghost" size="compact" onClick={() => signOut(true)}>
                      Sair de todos os aparelhos
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="compact"
                      disabled
                      disabledReason={
                        busy === 'sign-out-everywhere' ? 'Encerrando as sessões.' : 'Aguarde.'
                      }
                    >
                      {busy === 'sign-out-everywhere' ? 'Saindo…' : 'Sair de todos os aparelhos'}
                    </Button>
                  )}
                </span>
              </>
            }
          >
            Sair encerra a sessão neste navegador. Sair de todos encerra em todo lugar, inclusive
            aqui — para quando você esquece a conta aberta em outro computador.
          </Doing>
        </Section>

        {/*
          FR-077 — deletion is never one click away, and it is the last thing
          on the page. It keeps the page's own grammar; what sets it apart is
          the position and the one `destructive` control on the screen.
        */}
        <Section title="Excluir a conta" testId="delete-section">
          <Doing
            actions={
              <span data-testid="start-delete">
                {idle ? (
                  <Button
                    variant="destructive"
                    size="compact"
                    icon="trash"
                    onClick={() => setAsking('delete')}
                  >
                    Excluir minha conta
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="compact"
                    icon="trash"
                    disabled
                    disabledReason="Aguarde a ação em andamento."
                  >
                    Excluir minha conta
                  </Button>
                )}
              </span>
            }
          >
            Apaga para sempre sua conta e tudo o que você respondeu. Não dá para desfazer, e as
            lições da plataforma não são afetadas.
          </Doing>
        </Section>

        {/* § 12 — every outcome above is a change somebody may not be looking at. */}
        <LiveRegion message={notice} />
      </div>

      {/* ---------------------------------------------------- the decisions */}

      <Dialog
        open={asking === 'subscribe'}
        title="Escolher um plano"
        testId="subscribe-confirm"
        onClose={closeDialog}
        actions={
          <>
            <span data-testid="cancel-subscribe">
              <Button variant="ghost" onClick={closeDialog}>
                Agora não
              </Button>
            </span>
            <span data-testid="confirm-subscribe">
              {idle ? (
                <Button variant="primary" onClick={startPlan}>
                  Assinar {PLAN_NAMES[chosenPlan]}
                </Button>
              ) : (
                <Button variant="primary" disabled disabledReason="Ativando seu plano.">
                  Ativando…
                </Button>
              )}
            </span>
          </>
        }
      >
        <Text as="p" variant="body">
          Os dois planos dão o mesmo acesso: todas as lições publicadas, e as que chegarem enquanto
          a assinatura estiver ativa.
        </Text>

        <SettingChoice<PaidPlan>
          legend="Plano"
          legendHidden
          name="plan"
          value={chosenPlan}
          busy={!idle}
          onChange={setChosenPlan}
          options={[
            { value: 'monthly', label: 'Mensal', hint: 'Renova todo mês.' },
            { value: 'annual', label: 'Anual', hint: 'Renova uma vez por ano.' },
          ]}
        />

        {dialogAlert}

        {/* Said plainly rather than discovered later: nothing is charged yet. */}
        <Text as="p" variant="caption" tone="muted">
          A cobrança ainda não está ligada. Por enquanto o plano começa na hora, e você pode
          cancelar quando quiser.
        </Text>
      </Dialog>

      <Dialog
        open={asking === 'cancel-plan'}
        title="Cancelar o plano?"
        testId="cancel-plan-confirm"
        onClose={closeDialog}
        actions={
          <>
            <span data-testid="keep-plan">
              <Button variant="ghost" onClick={closeDialog}>
                Manter o plano
              </Button>
            </span>
            <span data-testid="confirm-cancel-plan">
              {idle ? (
                <Button variant="destructive" onClick={cancelPlan}>
                  Cancelar plano
                </Button>
              ) : (
                <Button variant="destructive" disabled disabledReason="Cancelando.">
                  Cancelando…
                </Button>
              )}
            </span>
          </>
        }
      >
        <Text as="p" variant="body">
          {plan?.renewsAt
            ? `Você continua com acesso até ${formatDate(plan.renewsAt)} — o período que já está pago. Depois disso sua conta volta para o plano gratuito.`
            : 'Você continua com acesso até o fim do período que já está pago. Depois disso sua conta volta para o plano gratuito.'}
        </Text>
        <Text as="p" variant="body-sm" tone="muted">
          Nada do que você já fez é apagado, e dá para reativar a qualquer momento antes dessa data.
        </Text>

        {dialogAlert}
      </Dialog>

      <Dialog
        open={asking === 'erase'}
        title="Apagar meus dados?"
        testId="erase-confirm"
        onClose={closeDialog}
        actions={
          <>
            <span data-testid="cancel-erase">
              {idle ? (
                <Button variant="ghost" onClick={closeDialog}>
                  Cancelar
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  disabled
                  disabledReason={
                    busy === 'erase' ? 'Apagando seus dados.' : 'Aguarde a ação em andamento.'
                  }
                >
                  Cancelar
                </Button>
              )}
            </span>
            <span data-testid="confirm-erase">
              {idle ? (
                <Button id="confirm-erase" variant="destructive" onClick={eraseData}>
                  Apagar meus dados
                </Button>
              ) : (
                <Button
                  id="confirm-erase"
                  variant="destructive"
                  disabled
                  disabledReason={
                    busy === 'erase' ? 'Apagando seus dados.' : 'Aguarde a ação em andamento.'
                  }
                >
                  {busy === 'erase' ? 'Apagando…' : 'Apagar meus dados'}
                </Button>
              )}
            </span>
          </>
        }
      >
        <Text as="p" variant="body">
          Some tudo o que você respondeu, suas lições concluídas, sua ofensiva, quem você segue,
          quem segue você e quem você bloqueou. Você volta para a primeira lição, com a mesma conta
          e as mesmas preferências.
        </Text>
        <Text as="p" variant="body-sm" tone="muted">
          Suas semanas já encerradas no ranking continuam como estão: elas são o registro de uma
          competição que já aconteceu, com outras pessoas dentro.
        </Text>

        {dialogAlert}

        {/*
          The copy of everything, offered at the moment it stops being
          theoretical — the same offer the deletion dialog makes.
        */}
        <div className={styles.confirm}>
          <span data-testid="export-before-erase">
            {idle ? (
              <Button variant="secondary" size="compact" icon="download" onClick={exportData}>
                {exported ? 'Baixar de novo' : 'Baixar meus dados antes'}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="compact"
                icon="download"
                disabled
                disabledReason={busy === 'export' ? 'Preparando o arquivo.' : 'Apagando.'}
              >
                {busy === 'export' ? 'Preparando…' : 'Baixar meus dados antes'}
              </Button>
            )}
          </span>
        </div>
      </Dialog>

      <Dialog
        open={asking === 'delete'}
        title="Excluir a conta?"
        testId="delete-confirm"
        onClose={closeDialog}
        actions={
          <>
            <span data-testid="cancel-delete">
              {idle ? (
                <Button variant="ghost" onClick={closeDialog}>
                  Cancelar
                </Button>
              ) : (
                <Button variant="ghost" disabled disabledReason="Excluindo sua conta.">
                  Cancelar
                </Button>
              )}
            </span>
            <span data-testid="confirm-delete-account">
              {typed === CONFIRMATION_WORD && idle ? (
                <Button id="confirm-delete-account" variant="destructive" onClick={deleteAccount}>
                  Excluir para sempre
                </Button>
              ) : (
                <Button
                  id="confirm-delete-account"
                  variant="destructive"
                  disabled
                  disabledReason={
                    busy === 'delete'
                      ? 'Excluindo sua conta.'
                      : `Digite ${CONFIRMATION_WORD} para habilitar.`
                  }
                >
                  {busy === 'delete' ? 'Excluindo…' : 'Excluir para sempre'}
                </Button>
              )}
            </span>
          </>
        }
      >
        <Text as="p" variant="body">
          Isto apaga para sempre sua conta, suas respostas, seu progresso e sua posição. Não é
          possível desfazer, e as lições da plataforma não são afetadas.
        </Text>

        {dialogAlert}

        <div className={styles.confirm}>
          <span data-testid="export-before-delete">
            {idle ? (
              <Button variant="secondary" size="compact" icon="download" onClick={exportData}>
                {exported ? 'Baixar de novo' : 'Baixar meus dados antes'}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="compact"
                icon="download"
                disabled
                disabledReason={
                  busy === 'export' ? 'Preparando o arquivo.' : 'Excluindo sua conta.'
                }
              >
                {busy === 'export' ? 'Preparando…' : 'Baixar meus dados antes'}
              </Button>
            )}
          </span>

          {/*
            `compact`, for the reason Field's own docstring gives: the default
            label is `subheading`, which is exactly the size of the dialog's
            title above it — a field louder than the question it answers.
          */}
          <Field
            size="compact"
            label={`Digite ${CONFIRMATION_WORD} para confirmar`}
            htmlFor="delete-confirmation"
          >
            <Input id="delete-confirmation" value={typed} onChange={setTyped} />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}

/**
 * The plan — the screen's ONE surface (§ 7), because it is the only thing on
 * `/conta` that is a state rather than a setting. What somebody is paying for,
 * until when, and the one verb that changes it.
 *
 * THE TWO DATES ARE NEVER THE SAME SENTENCE. "Renova em" is a promise that
 * money will be taken again; "termina em" is a promise that it will not. The
 * server sends whichever applies and this says exactly that one.
 */
function PlanCard({
  plan,
  status,
  idle,
  busy,
  onRetry,
  onSubscribe,
  onCancel,
  onReactivate,
}: {
  plan: SubscriptionResponse | null;
  status: 'loading' | 'ready' | 'unavailable';
  idle: boolean;
  busy: Busy;
  onRetry: () => void;
  onSubscribe: () => void;
  onCancel: () => void;
  onReactivate: () => void;
}) {
  const body = ((): {
    badge: ReactNode;
    title: string;
    line: string;
    action: ReactNode;
  } => {
    if (status === 'unavailable') {
      return {
        badge: null,
        title: 'Plano',
        line: 'Não foi possível carregar seu plano agora.',
        action: (
          <Button variant="secondary" size="compact" icon="rotate-ccw" onClick={onRetry}>
            Tentar de novo
          </Button>
        ),
      };
    }

    if (!plan) {
      return { badge: null, title: 'Plano', line: 'Carregando…', action: null };
    }

    if (plan.status === 'active' && plan.renewsAt) {
      return {
        badge: <Badge tone="success">Ativo</Badge>,
        title: PLAN_NAMES[plan.plan as PaidPlan],
        line: `Renova em ${formatDate(plan.renewsAt)}.`,
        action: idle ? (
          <Button variant="secondary" size="compact" onClick={onCancel}>
            Cancelar plano
          </Button>
        ) : (
          <Button variant="secondary" size="compact" disabled disabledReason="Aguarde.">
            Cancelar plano
          </Button>
        ),
      };
    }

    if (plan.status === 'canceling' && plan.endsAt) {
      return {
        badge: <Badge tone="near-miss">Cancelado</Badge>,
        title: PLAN_NAMES[plan.plan as PaidPlan],
        line: `Termina em ${formatDate(plan.endsAt)}. Até lá nada muda.`,
        action: idle ? (
          <Button variant="primary" size="compact" onClick={onReactivate}>
            Reativar plano
          </Button>
        ) : (
          <Button
            variant="primary"
            size="compact"
            disabled
            disabledReason={busy === 'reactivate-plan' ? 'Reativando.' : 'Aguarde.'}
          >
            {busy === 'reactivate-plan' ? 'Reativando…' : 'Reativar plano'}
          </Button>
        ),
      };
    }

    return {
      // No chip: the title already says the word, and a state chip that
      // repeats the heading under it is a label for nobody.
      badge: null,
      title: 'Gratuito',
      line:
        plan.status === 'expired' && plan.endsAt
          ? `Sua assinatura terminou em ${formatDate(plan.endsAt)}.`
          : 'Com acesso a todas as lições publicadas.',
      action: idle ? (
        <Button variant="secondary" size="compact" onClick={onSubscribe}>
          Assinar
        </Button>
      ) : (
        <Button variant="secondary" size="compact" disabled disabledReason="Aguarde.">
          Assinar
        </Button>
      ),
    };
  })();

  return (
    <Surface layer={1} radius="xl" inset>
      <div className={styles.plan} data-testid="plan-card">
        {body.badge}

        <Text as="p" variant="heading">
          {body.title}
        </Text>

        <Text as="p" variant="body-sm" tone="muted">
          {body.line}
        </Text>

        {/* How long this has been going on — the one fact a plan carries that
            no button on it can tell you. */}
        {plan?.since && plan.status !== 'none' ? (
          <Text as="p" variant="caption" tone="muted">
            Assinante desde {formatDate(plan.since)}.
          </Text>
        ) : null}

        {body.action ? <div className={styles.verbs}>{body.action}</div> : null}
      </div>
    </Surface>
  );
}
